import { useTranslation, Trans } from 'react-i18next'

import {
  Box,
  Text,
  Stack,
  Button,
  TextLink,
  IconInfoOff,
  Skeleton,
  Alert
} from '@machobear/design-system'
import { vars, breakpoints } from '@machobear/design-system/css'
import { useDispatch } from 'react-redux'
import { showPending } from '@/features/Listing/publish-modal'
import { useGetSponsoredListing } from '@/hooks/use-sponsored-listing'
import { showListingHiddenModal } from '@/features/Listing/listing-hidden-modal'
import config from '@/config'
import useMediaQuery from '@/hooks/use-media-query'
import ChecklistItem from './checklist-item'
import VerifiedBadge from './verified-badge.svg?react'

const { APP_PATH } = config

interface NextStepsModalProps {
  listingId: string
}

const PublishListingButton = ({ stateMachine }: { stateMachine: string }) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.tablet}px)`)
  const isPublished = stateMachine === 'published'
  const isPending = stateMachine === 'pending'

  const getButtonText = () => {
    if (isPublished) return t('l.listing.next_steps.published.button', 'Published')
    else if (isPending) return t('l.listing.next_steps.pending.button', 'Pending')
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      style={{ width: displayMobile ? '100%' : 'auto' }}
    >
      <Box style={{ width: displayMobile ? '100%' : '130px' }}>
        <Button disabled>{getButtonText()}</Button>
      </Box>
      {!isPublished && (
        <Box paddingTop="xsmall">
          <Text tone="link" size="xsmall">
            <TextLink
              id="application-sponsored-listings-whats-next-anchor-whatisthis-click"
              href="#"
              onClick={() => showPending({ isPublished })}
              iconPosition="leading"
              icon={<IconInfoOff />}
            >
              {t('l.listing.next_steps.what_is_this.button', 'What is this?')}
            </TextLink>
          </Text>
        </Box>
      )}
    </Box>
  )
}

const VerifyListingButton = ({
  isVerified,
  listingId
}: {
  isVerified: boolean
  listingId: string
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.tablet}px)`)

  const getButtonText = () => {
    if (isVerified) return t('common.verified', 'Verified')
    else return t('l.listing.next_steps.verify.button', 'Verify')
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="full"
      style={{ maxWidth: displayMobile ? 'unset' : '130px' }}
    >
      <Button
        id="application-sponsored-listings-whats-next-btn-verify-listing-click"
        disabled={isVerified}
        onClick={() => window.open(`${APP_PATH}/listing/verify/${listingId}`)}
      >
        {getButtonText()}
      </Button>
    </Box>
  )
}

const NextStepsModalContent = ({ listingId }: NextStepsModalProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { data: sponsoredListing, refetch, isLoading } = useGetSponsoredListing(listingId)
  const isVerified = sponsoredListing?.verified_state_machine === 'approved'
  const stateMachine = sponsoredListing?.state_machine
  const isPublished = stateMachine === 'published'
  const isHidden = !!Number(sponsoredListing?.is_hidden)

  const handleUnhide = async () => {
    await dispatch.listing.update({
      listing_id: listingId,
      is_hidden: 0
    })
    await refetch()
  }

  return (
    <Stack space="gutter">
      <Box>
        <Stack space="xsmall">
          <Text weight="strong">{t('l.listing.next_steps.what_next.title', "What's Next?")}</Text>
          <Text>
            {t(
              'l.listing.next_steps.what_next.description',
              `Complete these steps to ensure your listing is sponsored as scheduled.`
            )}
          </Text>
        </Stack>
      </Box>
      <Box>
        <Stack space="small">
          {isLoading ? (
            <Skeleton.Shape height={104} />
          ) : (
            <ChecklistItem
              index={1}
              title={t('l.listing.next_steps.publish_listing.heading', 'Publish your listing')}
              subtitle={t(
                'l.listing.next_steps.listing_sponsored.content',
                'Your listing must be published to be visible to our renters.'
              )}
              isComplete={isPublished}
            >
              <PublishListingButton stateMachine={stateMachine} />
            </ChecklistItem>
          )}
          {isLoading ? (
            <Skeleton.Shape height={104} />
          ) : (
            <ChecklistItem
              index={2}
              title={
                <Box display="flex" alignItems="center">
                  {t('l.listing.next_steps.verify_listing.title', 'Verify your listing')}&nbsp;
                  <VerifiedBadge />
                </Box>
              }
              subtitle={
                isVerified ? (
                  t(
                    'l.listing.next_steps.verified_complete_only_sponsored.text',
                    `Trust and security are our priority. Your listing must be verified in order to sponsor it.`
                  )
                ) : (
                  <Trans i18nKey="l.listing.next_steps.verified_only_sponsored.text" t={t}>
                    Trust and security are our priority. Your listing must be{' '}
                    <TextLink
                      target="_blank"
                      id="application-sponsored-listings-whats-next-anchor-verifiedlisting-click"
                      href="https://liv.rent/blog/livrent/rental-verification-process/"
                    >
                      verified
                    </TextLink>{' '}
                    in order to sponsor it.
                  </Trans>
                )
              }
              isComplete={isVerified}
            >
              <VerifyListingButton isVerified={isVerified} listingId={listingId} />
            </ChecklistItem>
          )}
        </Stack>
      </Box>

      {isHidden && (
        <Alert tone="caution">
          <Box display="flex" flexDirection="column">
            <Box marginBottom="xxsmall">
              <Text weight="strong" size="small">
                {t('l.listing.next_steps.hidden_listing.title', 'Hidden Listing')}
              </Text>
            </Box>
            <Text size="small">
              {t(
                'l.listing.next_steps.hidden_listing.text',
                'Your listing is hidden from public search. '
              )}
              <Box component="span" style={{ color: vars.foregroundColor.link }}>
                <TextLink href="#" onClick={() => showListingHiddenModal({ handleUnhide })}>
                  {t('l.listing.next_steps.unhide_now.button', 'Unhide now')}
                </TextLink>
              </Box>
            </Text>
          </Box>
        </Alert>
      )}
      <Box>
        <Text size="small">
          <Trans i18nKey="l.promotions.view.youre_all_set.text_3">
            If you have any questions, please contact{' '}
            <TextLink href="mailto:support@liv.rent">support@liv.rent</TextLink>.
          </Trans>
        </Text>
      </Box>
    </Stack>
  )
}

export default NextStepsModalContent
